/**
 * SpeedOverlay — floating speedometer while Driving Mode is on.
 *
 * Design:
 *   • Bottom-right of the viewport, well out of the way of the map + feed.
 *   • Huge digit readout so the driver can glance at it without reading.
 *   • Turns red + pulses when the user is over their configured limit.
 *   • Long-press (or tap, on desktop) the limit chip to edit the limit.
 *   • Hidden entirely when Driving Mode is off, or when speed is unknown
 *     (first few seconds before the GPS fix stabilises).
 *
 * Z-index 40 puts it above the LiveNowBanner (z-30) but below toast (z-50)
 * so TTS over-speed warnings still pop visibly on top.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export default function SpeedOverlay({ enabled, currentSpeedKph, speedLimitKph, isOverSpeed, setSpeedLimitKph }) {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(String(speedLimitKph));

  if (!enabled) return null;
  // Hide until we actually have a GPS fix with speed (first few seconds after
  // enable the device reports null speed).
  if (typeof currentSpeedKph !== "number" || isNaN(currentSpeedKph)) return null;

  const display = Math.round(currentSpeedKph);

  const saveLimit = () => {
    const kph = parseInt(draft, 10);
    if (Number.isFinite(kph) && kph >= 30 && kph <= 250) {
      setSpeedLimitKph(kph);
      setEditOpen(false);
    }
  };

  return (
    <>
      <div
        className={`fixed bottom-6 right-6 z-40 rounded-sm border shadow-2xl select-none transition-colors ${
          isOverSpeed
            ? "bg-red-600 border-red-700 text-white animate-pulse"
            : "bg-white/95 dark:bg-zinc-900/95 border-gray-900 dark:border-zinc-700 text-gray-900 dark:text-gray-100"
        }`}
        style={{ backdropFilter: "blur(6px)" }}
        data-testid="speed-overlay"
        aria-label={isOverSpeed ? t("driving.over_speed") : t("driving.speed")}
        role="status"
        aria-live={isOverSpeed ? "assertive" : "polite"}
      >
        <div className="px-5 pt-3 pb-1 flex items-center gap-2">
          <Gauge className={`w-4 h-4 ${isOverSpeed ? "text-white" : "text-red-600"}`} />
          <span className={`text-[10px] uppercase tracking-[0.25em] font-bold font-body ${isOverSpeed ? "text-white/90" : "text-gray-500 dark:text-gray-300"}`}>
            {isOverSpeed ? t("driving.over_speed") : t("driving.speed")}
          </span>
        </div>
        <div className="px-5 pb-1 flex items-baseline gap-2 leading-none">
          <span
            className="font-heading font-black tracking-tighter tabular-nums text-5xl sm:text-6xl"
            data-testid="speed-overlay-value"
          >
            {display}
          </span>
          <span className={`text-xs font-body ${isOverSpeed ? "text-white/80" : "text-gray-400 dark:text-gray-400"}`}>
            km/h
          </span>
        </div>
        <button
          type="button"
          onClick={() => { setDraft(String(speedLimitKph)); setEditOpen(true); }}
          className={`w-full px-5 pb-3 pt-1 text-[10px] flex items-center justify-center gap-1.5 font-body tracking-widest uppercase border-t ${
            isOverSpeed
              ? "border-white/30 text-white/90 hover:bg-white/10"
              : "border-gray-100 dark:border-zinc-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800"
          }`}
          data-testid="speed-overlay-edit-limit"
          aria-label={t("driving.edit_limit")}
        >
          <Settings className="w-3 h-3" />
          {t("driving.limit_short", { kph: speedLimitKph })}
        </button>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xs rounded-sm" data-testid="speed-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tighter">{t("driving.speed_limit_label")}</DialogTitle>
            <DialogDescription className="font-body">
              {t("driving.speed_limit_dialog_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={30}
              max={250}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="rounded-sm font-mono text-lg font-bold"
              data-testid="speed-limit-input"
              autoFocus
            />
            <span className="text-sm text-gray-500 font-body">km/h</span>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={saveLimit} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="speed-limit-save">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
